import { Link } from "react-router-dom";
import { BadgeCheck, Bath, BedDouble, Heart, MapPin, Ruler } from "lucide-react";
import PriceTag from "./PriceTag";
import { useI18n } from "@/i18n/I18nProvider";
import { useFavorite } from "@/context/Favorites";
import { useCatalog } from "@/hooks/useData";
import { fileSrc, onImgError, PLACEHOLDER_SRC } from "@/lib/api";
import { areaLabel } from "@/lib/format";
import { AgencyChip } from "@/components/agency/AgencyBits";

export function FavoriteButton({ pid, className = "" }) {
  const { active, onToggle } = useFavorite(pid);
  return (
    <button onClick={onToggle} data-testid={`favorite-btn-${pid}`} aria-pressed={active}
      className={`grid place-items-center w-9 h-9 rounded-full bg-white/95 dark:bg-[#131E2E]/90 shadow transition-transform hover:scale-105 ${className}`}>
      <Heart className={`w-[18px] h-[18px] ${active ? "fill-red-500 text-red-500" : "text-navy"}`} />
    </button>
  );
}

export default function PropertyCard({ p }) {
  const { t, nm } = useI18n();
  const catalog = useCatalog();
  const place = [p.location?.district, p.location?.governorate].filter(Boolean).map(nm).join("، ");
  return (
    <Link to={`/property/${p.ref}`} data-testid="property-card-container"
      className="group block rounded-2xl border border-line bg-white overflow-hidden transition-shadow hover:shadow-xl hover:shadow-navy/10">
      <div className="relative aspect-[4/3] bg-sand overflow-hidden">
        <img src={fileSrc(p.cover) || PLACEHOLDER_SRC} onError={onImgError} alt={p.title} loading="lazy" decoding="async" data-testid="property-card-image" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-3 start-3 rounded-full bg-navy/90 px-3 py-1 text-xs font-bold text-white">{t(`purpose.${p.purpose}`)}</span>
        {p.availability !== "available" && <span className="absolute bottom-3 start-3 rounded-full bg-gold px-3 py-1 text-xs font-bold text-white">{t(`availability.${p.availability}`)}</span>}
        <FavoriteButton pid={p.id} className="absolute top-3 end-3" />
      </div>
      <div className="p-4 space-y-2.5">
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-gold font-semibold">{t(`type.${p.type}`)}</span>
          <span data-testid="property-card-ref" className="font-mono text-ink-muted" dir="ltr">{p.ref}</span>
        </div>
        <h3 className="font-display font-bold text-navy line-clamp-1">{p.title}</h3>
        <p className="flex items-center gap-1 text-xs text-ink-muted line-clamp-1"><MapPin className="w-3.5 h-3.5 shrink-0" />{place}</p>
        <PriceTag price={p.price} testId="property-card-price" />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2.5 text-xs text-ink-muted">
          {p.bedrooms != null && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{p.bedrooms} {t("card.beds")}</span>}
          {p.bathrooms != null && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.bathrooms} {t("card.baths")}</span>}
          <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" />{areaLabel(p.area, catalog?.units, nm)}</span>
          {p.advertiser_verified && <span className="flex items-center gap-1 text-emerald-700 font-semibold"><BadgeCheck className="w-3.5 h-3.5" />{t("card.verified")}</span>}
        </div>
        {p.agency && <div className="border-t border-line pt-2.5"><AgencyChip agency={p.agency} /></div>}
      </div>
    </Link>
  );
}
